/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { Download, FileText } from 'lucide-react';
import { useCallback, useEffect, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../components/ui/button.js';
import { type IntakeView, intakeApi } from '../../lib/api/intake.js';
import {
  useGenerateIntakePdf,
  useIntakeDocumentMeta,
  useSignIntake,
} from '../../lib/queries/intake.js';

import { INTAKE_CARD_CLASS } from './intake-form-utils.js';
import { SignaturePad } from './signature-pad.js';

/** Trigger a browser download of a fetched PDF blob. */
async function downloadIntake(clientId: string, fileName: string): Promise<void> {
  const blob = await intakeApi.downloadDocument(clientId);
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

/** Admin: capture an e-signature, generate the intake PDF, and download it. */
export function IntakeDocumentsPanel({ view }: { readonly view: IntakeView }): JSX.Element {
  const { t } = useTranslation();
  const sign = useSignIntake(view.clientId);
  const generate = useGenerateIntakePdf(view.clientId);
  const [generating, setGenerating] = useState(false);
  const meta = useIntakeDocumentMeta(view.clientId, generating);
  const available = meta.data?.available ?? false;

  useEffect(() => {
    if (available) setGenerating(false);
  }, [available]);

  const onCapture = useCallback(
    (dataUrl: string) => {
      sign.mutate({ signatureDataUrl: dataUrl });
    },
    [sign],
  );
  const onGenerate = useCallback(() => {
    setGenerating(true);
    generate.mutate();
  }, [generate]);
  const onDownload = useCallback(() => {
    void downloadIntake(view.clientId, meta.data?.fileName ?? 'patient-intake.pdf');
  }, [view.clientId, meta.data]);

  return (
    <div className={INTAKE_CARD_CLASS}>
      <h3 className="mb-1 text-base font-semibold text-slate-800 dark:text-white">
        {t('intake.documents.title')}
      </h3>
      <p className="mb-4 text-sm text-slate-400">{t('intake.documents.subtitle')}</p>

      {view.signatureDataUrl !== null && (
        <div className="mb-4">
          <p className="mb-1 text-xs font-medium text-slate-500">{t('intake.sign.current')}</p>
          <img
            src={view.signatureDataUrl}
            alt={t('intake.sign.canvasLabel')}
            className="max-h-20 rounded-lg border border-slate-200 bg-white p-1 dark:border-slate-700"
          />
        </div>
      )}

      <p className="mb-1 text-sm font-medium text-slate-700 dark:text-slate-300">
        {view.signatureDataUrl !== null ? t('intake.sign.replace') : t('intake.sign.capture')}
      </p>
      <SignaturePad onCapture={onCapture} saving={sign.isPending} />

      <div className="mt-6 flex flex-wrap items-center gap-3 border-t border-slate-100 pt-4 dark:border-slate-800">
        <Button type="button" onClick={onGenerate} disabled={generate.isPending || generating}>
          <FileText className="h-4 w-4" aria-hidden="true" />
          {generating ? t('intake.documents.generating') : t('intake.documents.generate')}
        </Button>
        {available && (
          <Button type="button" variant="outline" onClick={onDownload}>
            <Download className="h-4 w-4" aria-hidden="true" />
            {t('intake.documents.download')}
          </Button>
        )}
      </div>
    </div>
  );
}
